import React, { useEffect, useMemo, useState } from 'react';
import { AlertTriangle, ExternalLink, X } from 'lucide-react';
import { dynamicFieldsApi } from '../dynamicFields/api';
import { ENTRY_KIND_LABELS, LibraryCatalogEntry } from '../dynamicFields/types';
import { iconButton, primaryButton, secondaryButton, Skeleton } from '../ui';
import { ROUTES } from '../../../config/links';

type LibraryFieldPickerProps = {
  sensorType: string;
  category: string;
  /** Entry keys already selected for the step. */
  selected: string[];
  /** The form has a field named `range`, which the calibration points group needs. */
  hasRangeField: boolean;
  onSave: (entryKeys: string[]) => void;
  onClose: () => void;
};

/** Choose which published library entries a step will show at runtime. */
export default function LibraryFieldPicker({ sensorType, category, selected, hasRangeField, onSave, onClose }: LibraryFieldPickerProps) {
  const [catalog, setCatalog] = useState<LibraryCatalogEntry[]>();
  const [error, setError] = useState<string>();
  const [chosen, setChosen] = useState<Set<string>>(() => new Set(selected));

  useEffect(() => {
    let cancelled = false;
    dynamicFieldsApi.catalog({ sensor_type: sensorType, category })
      .then((rows) => { if (!cancelled) setCatalog(rows); })
      .catch((e) => { if (!cancelled) setError(e?.response?.data?.detail?.message || e?.response?.data?.detail || e?.message || 'Failed to load the library'); });
    return () => { cancelled = true; };
  }, [sensorType, category]);

  useEffect(() => {
    const onKey = (event: KeyboardEvent) => { if (event.key === 'Escape') onClose(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  const toggle = (key: string) => setChosen((current) => {
    const next = new Set(current);
    if (next.has(key)) next.delete(key); else next.add(key);
    return next;
  });

  // Keys the library no longer offers (removed or unpublished) stay visible so they can be dropped.
  const missing = useMemo(() => (catalog ? selected.filter((key) => !catalog.some((c) => c.key === key)) : []), [catalog, selected]);
  const pointsChosen = !!catalog?.some((c) => c.is_group && chosen.has(c.key));

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/30 p-4 backdrop-blur-[2px]">
      <div role="dialog" aria-modal="true" aria-labelledby="library-picker-title" className="flex max-h-[88vh] w-full max-w-lg flex-col rounded-xl border border-slate-200 bg-white shadow-xl">
        <div className="flex items-start justify-between gap-3 border-b border-slate-100 p-4">
          <div>
            <h2 id="library-picker-title" className="text-sm font-semibold text-slate-900">Select dynamic fields</h2>
            <p className="mt-1 text-[11px] leading-4 text-slate-500">
              Published entries for <strong>{sensorType} / {category}</strong>. The selected fields are resolved when the step opens, so later library changes apply to this form once they are published.
            </p>
          </div>
          <button type="button" className={iconButton} onClick={onClose} aria-label="Close"><X className="h-3.5 w-3.5" /></button>
        </div>

        <div className="min-h-0 flex-1 space-y-2 overflow-y-auto p-4">
          {!catalog && !error && Array.from({ length: 4 }).map((_, i) => <Skeleton key={i} className="h-12 w-full" />)}
          {error && <p role="alert" className="rounded-lg border border-red-200 bg-red-50 p-3 text-[12px] text-red-700">{error}</p>}
          {catalog && catalog.length === 0 && (
            <div className="rounded-lg border border-dashed border-slate-200 bg-slate-50 p-6 text-center text-[12px] text-slate-500">
              This sensor type and category has no published dynamic fields yet.
              <a className="mt-2 inline-flex items-center gap-1 font-medium text-primary-700 underline" href={ROUTES.dynamicFieldsEdit(sensorType, category)} target="_blank" rel="noreferrer">
                Define them <ExternalLink className="h-3 w-3" />
              </a>
            </div>
          )}
          {catalog && catalog.length > 0 && (
            <div className="flex items-center justify-between text-[11px] text-slate-500">
              <span>{chosen.size} selected</span>
              <span className="flex gap-3">
                <button type="button" className="font-medium text-primary-700 hover:underline" onClick={() => setChosen(new Set(catalog.map((c) => c.key)))}>Select all</button>
                <button type="button" className="font-medium text-slate-600 hover:underline" onClick={() => setChosen(new Set())}>Clear</button>
              </span>
            </div>
          )}
          {catalog?.map((entry) => (
            <label key={entry.key} className={`flex cursor-pointer items-start gap-2.5 rounded-lg border p-3 transition ${chosen.has(entry.key) ? 'border-primary-300 bg-primary-50/40' : 'border-slate-200 hover:border-slate-300'}`}>
              <input className="mt-0.5 h-4 w-4 rounded border-slate-300 text-primary-600 focus:ring-primary-500" type="checkbox" checked={chosen.has(entry.key)} onChange={() => toggle(entry.key)} />
              <span className="min-w-0 flex-1">
                <span className="flex items-center gap-2">
                  <span className="truncate text-[12px] font-semibold text-slate-800">{entry.label}</span>
                  <span className="shrink-0 rounded-full bg-slate-100 px-1.5 py-0.5 text-[9px] font-medium uppercase tracking-wide text-slate-500">{ENTRY_KIND_LABELS[entry.kind]}</span>
                </span>
                <span className="mt-0.5 block text-[10px] text-slate-500">
                  {entry.is_group ? 'A group of fields, created when the form runs from the sensor range.' : <>Field key <code>{entry.key}</code></>}
                </span>
              </span>
            </label>
          ))}
          {missing.map((key) => (
            <label key={key} className="flex cursor-pointer items-start gap-2.5 rounded-lg border border-amber-300 bg-amber-50 p-3">
              <input className="mt-0.5 h-4 w-4 rounded border-slate-300" type="checkbox" checked={chosen.has(key)} onChange={() => toggle(key)} />
              <span className="text-[11px] text-amber-800"><code>{key}</code> is no longer published in the library. Untick it to remove it; the form cannot be published while it is selected.</span>
            </label>
          ))}
          {pointsChosen && !hasRangeField && (
            <p className="flex items-start gap-1.5 rounded-lg bg-amber-50 p-2.5 text-[11px] text-amber-800"><AlertTriangle className="mt-0.5 h-3.5 w-3.5 shrink-0" /> The calibration points are scaled by the sensor range, but this form has no field with the key <code>range</code>. Without it the unscaled base values are used.</p>
          )}
        </div>

        <div className="flex justify-end gap-2 border-t border-slate-100 p-4">
          <button type="button" className={secondaryButton} onClick={onClose}>Cancel</button>
          <button type="button" className={primaryButton} disabled={!catalog} onClick={() => onSave(Array.from(chosen))}>
            {chosen.size === 0 ? 'Remove dynamic fields' : `Use ${chosen.size} ${chosen.size === 1 ? 'entry' : 'entries'}`}
          </button>
        </div>
      </div>
    </div>
  );
}
